import {
  UseMutateAsyncFunction,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { createContext, ReactNode, useContext, useState } from "react";
import { AmbientConversation } from "../api/ambientConversation.type";
import {
  completeAmbientConversation,
  createAmbientConversation,
  deleteAmbientConversation,
  getAmbientConversation,
  getConversationAnalysis,
  updateAmbientConversation,
} from "../api/ambientConversations.api";
import { useToastContext } from "../containers/ToastContextWrapper";
import { getErrorMessage } from "../utils/errors";
import { useUserContext } from "./UserContextWrapper";
import { useLoadAmbientConvos } from "./useLoadAmbientConvos";

interface AmbientConversationContextType {
  isLoading: boolean;
  error: any;

  createConversation: UseMutateAsyncFunction<
    AmbientConversation,
    unknown,
    void,
    unknown
  >;
  setSelectedConversationId: (id: string) => void;
  selectedConversation: AmbientConversation | null;
  updateConversation: (convoId: string) => Promise<AmbientConversation>;
  completeConversation: (convoId: string) => Promise<AmbientConversation>;
  deleteConversation: (conversationId: string) => Promise<void>;
  refetchConversations: () => void;
  getConvoAnalysis: (convoId: string) => Promise<AmbientConversation>;
  isAnalysisLoading: boolean;
}

const AmbientConversationContext =
  createContext<AmbientConversationContextType>({
    isLoading: false,
    error: null,
    createConversation: async () => ({} as AmbientConversation),
    setSelectedConversationId: () => {},
    selectedConversation: null,
    updateConversation: async () => ({} as AmbientConversation),
    completeConversation: async () => ({} as AmbientConversation),
    deleteConversation: async () => {},
    refetchConversations: () => {},
    getConvoAnalysis: async () => ({} as AmbientConversation),
    isAnalysisLoading: false,
  });

export const useAmbientConversationContext = () => {
  const context = useContext(AmbientConversationContext);
  if (!context) {
    throw new Error(
      "useAmbientConversationContext must be used within an AmbientConversationContextWrapper"
    );
  }
  return context;
};

export const AmbientConversationContextWrapper = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { user } = useUserContext();
  const { showToast } = useToastContext();
  const queryClient = useQueryClient();

  const { isLoading, error, refetchConversations } = useLoadAmbientConvos();

  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);

  const { mutateAsync: createConversation, error: createError } = useMutation({
    mutationFn: createAmbientConversation,
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: ["ambient-conversations", user?.id],
      });
    },
    onError(error) {
      console.error("Error creating ambient conversation:", createError);
      showToast({
        title: "Error",
        description: `Failed to create ambient conversation. ${error.message}`,
        type: "error",
      });
    },
  });

  const {
    mutateAsync: getConvoAnalysis,
    isPending: isAnalysisLoading,
    error: analysisError,
  } = useMutation({
    mutationFn: (convoId: string) => getConversationAnalysis(convoId),
    onMutate: () => {
      showToast({
        title: "AI Analysis Started",
        description: "Processing your conversation...",
        type: "info",
      });
    },
    onError(error) {
      console.error("Error getting conversation analysis:", analysisError);
      showToast({
        title: "Analysis Failed",
        description: `Failed to process AI analysis. Please try again. ${error.message}`,
        type: "error",
      });
    },
    onSuccess() {
      showToast({
        title: "AI Analysis Complete",
        description: "Your conversation has been analyzed",
        type: "success",
      });
    },
  });

  // Selected conversation: useQuery with enabled
  const {
    data: selectedConversation,
    isLoading: isSelectedConversationLoading,
  } = useQuery({
    queryKey: ["ambient-conversation", selectedConversationId],
    queryFn: () => getAmbientConversation(selectedConversationId ?? ""),
    enabled: !!selectedConversationId,
  });

  // Update conversation mutation
  const { mutateAsync: updateConversation } = useMutation({
    mutationFn: (convoId: string) => updateAmbientConversation(convoId),
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: ["ambient-conversations", user?.id],
      });
    },
    onError(error) {
      console.error("Error updating ambient conversation:", error);
      showToast({
        title: "Error",
        description: getErrorMessage(error),
        type: "error",
      });
    },
    onSuccess() {
      showToast({
        title: "Conversation Updated",
        description: "Ambient conversation updated successfully",
        type: "success",
      });
    },
  });

  // Complete conversation mutation (silent/internal)
  const { mutateAsync: completeConversation } = useMutation({
    mutationFn: (id: string) => completeAmbientConversation(id),
    onSettled: (_data, _error, id) => {
      queryClient.invalidateQueries({
        queryKey: ["ambient-conversations", user?.id],
      });
      if (id) {
        queryClient.invalidateQueries({
          queryKey: ["ambient-conversation", id],
        });
      }
    },
  });

  // Delete conversation mutation
  const { mutateAsync: deleteConversationMutation } = useMutation({
    mutationFn: deleteAmbientConversation,
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: ["ambient-conversations", user?.id],
      });
    },
    onError(error) {
      console.error("Error deleting ambient conversation:", error);
      showToast({
        title: "Error",
        description: "Failed to delete ambient conversation",
        type: "error",
      });
    },
    onSuccess() {
      showToast({
        title: "Conversation Deleted",
        description: "Ambient conversation deleted successfully",
        type: "success",
      });
    },
  });

  // selectedConversation is automatically managed by useQuery above

  const deleteConversation = async (conversationId: string) => {
    await deleteConversationMutation(conversationId);
  };

  const value: AmbientConversationContextType = {
    isLoading: isLoading || isSelectedConversationLoading,
    error,
    createConversation,
    setSelectedConversationId,
    selectedConversation: selectedConversation ?? null,
    updateConversation,
    completeConversation,
    deleteConversation,
    refetchConversations,
    getConvoAnalysis,
    isAnalysisLoading,
  };

  return (
    <AmbientConversationContext.Provider value={value}>
      {children}
    </AmbientConversationContext.Provider>
  );
};

export const AmbientConversationsProvider = AmbientConversationContextWrapper;
export const useAmbientConversations = useAmbientConversationContext;
